import { useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Check, RefreshCw, AlertTriangle, ImageIcon, SprayCan, FileText } from "lucide-react";

function scrollToPhoto(photoId) {
  const el = document.querySelector(`[data-testid="detail-photo-${photoId}"]`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-brand", "ring-offset-2");
  setTimeout(() => el.classList.remove("ring-2", "ring-brand", "ring-offset-2"), 2000);
}

export function CleaningChecklist({ propertyId, photosById = {}, reloadSignal }) {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/cleaning-checklist`);
      setChecklist(data || null);
    } catch (e) {
      console.error("load cleaning checklist failed", e);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);
  // Reload when a lease scan/edit auto-seeds new cleaning tasks.
  useEffect(() => { if (reloadSignal) load(); }, [reloadSignal, load]);

  const generate = async () => {
    setGenerating(true);
    setError(false);
    try {
      const { data } = await api.post(`/properties/${propertyId}/cleaning-checklist`);
      setChecklist(data);
    } catch (e) {
      console.error("generate cleaning checklist failed", e);
      setError(true);
    } finally {
      setGenerating(false);
    }
  };

  const toggle = async (index) => {
    // optimistic
    setChecklist((c) => {
      const items = c.items.map((it, i) => i === index ? { ...it, done: !it.done } : it);
      return { ...c, items };
    });
    try {
      await api.patch(`/cleaning-checklist/${checklist.id}/items/${index}`);
    } catch (e) {
      console.error("toggle cleaning item failed", e);
      setChecklist((c) => {
        const items = c.items.map((it, i) => i === index ? { ...it, done: !it.done } : it);
        return { ...c, items };
      });
    }
  };

  if (loading) return null;

  const Section = ({ children }) => (
    <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-5 sm:p-6" data-testid="cleaning-checklist-section">
      <div className="flex items-center gap-2">
        <SprayCan size={20} className="text-brand" />
        <h2 className="font-heading text-xl font-semibold text-navy">Prepare for move-out</h2>
      </div>
      <p className="mt-1 text-sm text-[#64748B]">AI turns your move-in photos into a room-by-room cleaning plan to help return the space to its documented condition.</p>
      {children}
    </div>
  );

  // Empty / generate state
  if (!checklist) {
    return (
      <Section>
        {error ? (
          <div className="mt-4 rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-4" data-testid="cleaning-error">
            <p className="flex items-center gap-2 text-sm font-semibold text-[#B91C1C]"><AlertTriangle size={16} /> AI is temporarily unavailable.</p>
            <Button data-testid="cleaning-retry-btn" onClick={generate} disabled={generating} className="mt-3 rounded-xl bg-brand hover:bg-brand-hover text-white">
              {generating ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing your move-in photos…</> : <><RefreshCw size={15} className="mr-2" /> Retry</>}
            </Button>
          </div>
        ) : (
          <Button data-testid="generate-cleaning-btn" onClick={generate} disabled={generating} className="mt-4 rounded-xl bg-brand hover:bg-brand-hover text-white h-11 px-5">
            {generating ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing your move-in photos…</> : <><Sparkles size={16} className="mr-2" /> Generate cleaning checklist</>}
          </Button>
        )}
      </Section>
    );
  }

  const items = checklist.items || [];
  const doneCount = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  // group by room preserving order
  const groups = [];
  const idxByRoom = {};
  items.forEach((it, i) => {
    if (!(it.room in idxByRoom)) { idxByRoom[it.room] = groups.length; groups.push({ room: it.room, entries: [] }); }
    groups[idxByRoom[it.room]].entries.push({ ...it, index: i });
  });

  return (
    <Section>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-[#64748B]" data-testid="cleaning-empty">Your move-in photos look clean — no restoration tasks were suggested.</p>
      ) : (
        <>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 h-2.5 rounded-full bg-[#F1F5F9] overflow-hidden">
              <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-sm font-bold text-navy shrink-0" data-testid="cleaning-progress">{doneCount} of {items.length} done</span>
          </div>

          <div className="mt-5 space-y-5">
            {groups.map((g) => {
              const isLease = g.room === "From your lease";
              return (
              <div key={g.room} data-testid={`cleaning-room-${g.room}`}>
                <h3 className="font-heading text-base font-bold text-navy flex items-center gap-1.5">
                  {isLease && <FileText size={15} className="text-brand" />}{g.room}
                  {isLease && <span className="text-xs font-medium text-[#64748B]">· required by your lease</span>}
                </h3>
                <div className="mt-2 space-y-2">
                  {g.entries.map((it) => {
                    const photo = photosById[it.source_photo_id];
                    return (
                      <div key={it.index} data-testid={`cleaning-item-${it.index}`} className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] px-3.5 py-2.5">
                        <button
                          data-testid={`cleaning-toggle-${it.index}`}
                          onClick={() => toggle(it.index)}
                          aria-pressed={it.done}
                          className={`grid place-items-center h-5 w-5 rounded-md border shrink-0 transition-colors ${it.done ? "bg-[#177245] border-[#177245] text-white" : "border-[#CBD5E1] hover:border-brand"}`}
                        >
                          {it.done && <Check size={13} strokeWidth={3} />}
                        </button>
                        <span className={`text-sm flex-1 min-w-0 ${it.done ? "text-[#94A3B8] line-through" : "text-navy"}`}>{it.task}</span>
                        {it.source === "lease" ? (
                          <span data-testid={`cleaning-lease-badge-${it.index}`} className="inline-flex items-center gap-1 rounded-full bg-amber-soft text-amber-ink border border-amber-border text-[11px] font-semibold px-2 py-0.5 shrink-0">
                            <FileText size={11} /> Lease
                          </span>
                        ) : photo ? (
                          <button
                            data-testid={`cleaning-photo-${it.index}`}
                            onClick={() => scrollToPhoto(it.source_photo_id)}
                            title="View source move-in photo"
                            className="h-9 w-9 rounded-lg overflow-hidden border border-[#E2E8F0] shrink-0 hover:ring-2 hover:ring-brand transition"
                          >
                            <img src={fileUrl(photo.storage_path)} alt="source" loading="lazy" className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <ImageIcon size={16} className="text-[#CBD5E1] shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        </>
      )}

      <Button data-testid="regenerate-cleaning-btn" variant="outline" onClick={generate} disabled={generating} className="mt-5 rounded-xl border-[#E2E8F0]">
        {generating ? <><Loader2 size={15} className="mr-2 animate-spin" /> Analyzing your move-in photos…</> : <><RefreshCw size={14} className="mr-2" /> Regenerate</>}
      </Button>
    </Section>
  );
}
